import React, { useEffect, useState } from 'react'
import { Button, Modal, Select } from '@surecompanies/core_components'
import { US_STATE_OPTIONS } from 'enrollment-platform/pages/PlanManagementV2/pages/PlanOverrides/constants'
import {
  SURECO_CSV_DATA_TYPE_OPTIONS,
  type SurecoCsvDataType,
} from '../constants'
import styles from '../PlanManagementV3.module.scss'

export interface PlanDataCsvDownloadParams {
  year: number
  dataType: SurecoCsvDataType
  states: string[]
}

interface Props {
  open: boolean
  year: number | undefined
  isDownloading: boolean
  downloadedBytes: number
  onClose: () => void
  onDownload: (params: PlanDataCsvDownloadParams) => void
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function DownloadPlanDataCsvModal({
  open,
  year,
  isDownloading,
  downloadedBytes,
  onClose,
  onDownload,
}: Props) {
  const [dataType, setDataType] = useState<SurecoCsvDataType | undefined>(
    undefined
  )
  const [states, setStates] = useState<string[]>([])

  // Reset on `open` itself, not on how the modal closes — a successful
  // download closes it from the parent (setIsModalOpen(false)) without ever
  // calling handleClose, so resetting there only would leave stale
  // selections behind for the next open. A failed download intentionally
  // leaves `open` true so the user can retry, which correctly skips this.
  useEffect(() => {
    if (open) return
    setDataType(undefined)
    setStates([])
  }, [open])

  const handleClose = () => {
    if (isDownloading) return
    onClose()
  }

  const handleDownload = () => {
    if (year === undefined || dataType === undefined || isDownloading) return
    onDownload({ year, dataType, states })
  }

  const isDownloadDisabled =
    year === undefined || dataType === undefined || isDownloading

  return (
    <Modal
      open={open}
      title="Download"
      maskClosable={false}
      closable={!isDownloading}
      onCancel={handleClose}
      footer={[
        <Button
          key="cancel"
          htmlType="button"
          disabled={isDownloading}
          onClick={handleClose}
        >
          Cancel
        </Button>,
        <Button
          key="confirm"
          htmlType="button"
          type="primary"
          disabled={isDownloadDisabled}
          loading={isDownloading}
          onClick={handleDownload}
        >
          Download
        </Button>,
      ]}
      centered
      destroyOnClose
    >
      <div className={styles.modalContent}>
        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>Year</span>
          <Select
            disabled
            value={year}
            options={
              year === undefined ? [] : [{ label: String(year), value: year }]
            }
          />
        </div>

        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>Data type</span>
          <Select
            placeholder="Select a data type"
            value={dataType}
            onChange={(v) => setDataType(v)}
            options={SURECO_CSV_DATA_TYPE_OPTIONS}
          />
        </div>

        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>State</span>
          <Select
            mode="multiple"
            placeholder="Nationwide"
            showSearch
            optionFilterProp="label"
            value={states}
            onChange={(v) => setStates(v)}
            options={US_STATE_OPTIONS}
            maxTagCount="responsive"
            dropdownRender={(menu) => (
              <>
                <div className={styles.selectAllRow}>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setStates([])}
                  >
                    Clear all
                  </Button>
                </div>
                {menu}
              </>
            )}
          />
        </div>

        {isDownloading && (
          <span className={styles.modalFieldLabel}>
            Downloading… {formatBytes(downloadedBytes)}
          </span>
        )}
      </div>
    </Modal>
  )
}
